import { Button, Card, Collapse, Typography, Avatar, Tag } from "antd"
import { ScheduledEvent, Timesheet, CrewMember, VisitWorkareaStatusRecord } from "../scheduleApi"
import { getInitials } from "../scheduleModel"
import { useWorkareaGroupsQuery } from "../../../domain/workareas/groupsQueries"
import { emailsMatch } from "../crewModel"

const { Text } = Typography

type Props = {
  event: ScheduledEvent | null
  jobId: number
  timesheets: Timesheet[]
  crew: CrewMember[]
  crewLeadEmail: string | null
  workareaStatuses: VisitWorkareaStatusRecord[]
  onManageClick: () => void
}

const formatTime = (totalHours: number): string => {
  if (totalHours <= 0) return '0m'
  const hours = Math.floor(totalHours)
  const minutes = Math.floor((totalHours - hours) * 60)
  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

const JobDrawerWorkedCrew = ({ event, jobId, timesheets, crew, crewLeadEmail, workareaStatuses, onManageClick }: Props) => {
  const { data: workareaGroups } = useWorkareaGroupsQuery(jobId)

  const visitTimesheets = timesheets

  const crewMemberIds = [...new Set(visitTimesheets.map(t => t.crewMemberId))]

  const workedCrewMembers = crewMemberIds
    .map(id => crew.find(c => c.id === id))
    .filter((c): c is CrewMember => c !== undefined)

  const getWorkareaName = (workareaId: number): string => {
    const workareaStatus = workareaStatuses.find(ws => ws.workareaId === workareaId)
    if (workareaStatus?.workareaName) {
      return workareaStatus.workareaName
    }
    if (!event) {
      return `Work Area ${workareaId}`
    }
    const workareaIds = event.workAreaIds?.split(',').map(id => parseInt(id.trim())) ?? []
    const workareaNames = event.workAreaNames?.split(',').map(name => name.trim()) ?? []
    const index = workareaIds.indexOf(workareaId)
    if (index !== -1 && workareaNames[index]) {
      return workareaNames[index]
    }
    const workareaFromGroups = workareaGroups?.flatMap(g => g.workareas).find(w => w.id === workareaId)
    if (workareaFromGroups?.name) {
      return workareaFromGroups.name
    }
    return `Work Area ${workareaId}`
  }

  return <>
    <div style={{ marginBottom: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <Text style={{ fontSize: '14px' }}>
            Worked Crew
          </Text>
          <span>
            <Button onClick={onManageClick}>
              Manage
            </Button>
          </span>
        </div>
      </div>

      {workedCrewMembers.length > 0 ? (
        <Card size="small" style={{ padding: 0 }} styles={{ body: { padding: 0 } }}>
          <Collapse
            bordered={false}
            expandIconPosition="start"
            style={{ background: 'transparent' }}
            items={workedCrewMembers.map(member => {
              const memberTimesheets = visitTimesheets.filter(t => t.crewMemberId === member.id)
              const isCrewLead = emailsMatch(member.userEmail, crewLeadEmail)
              const totalMemberTime = memberTimesheets.reduce((sum, t) => sum + t.totalTime, 0)

              const workareaGroups = memberTimesheets.reduce((acc, t) => {
                const workareaId = t.projectWorkareaId ?? 0
                if (!acc[workareaId]) {
                  acc[workareaId] = { timesheets: [], totalTime: 0 }
                }
                acc[workareaId].timesheets.push(t)
                acc[workareaId].totalTime += t.totalTime
                return acc
              }, {} as Record<number, { timesheets: Timesheet[], totalTime: number }>)

              return {
                key: member.id,
                label: (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                    <Avatar
                      src={member.hasStockAvatar ? undefined : member.avatar}
                      size={32}
                      style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
                    >
                      {getInitials(member.name)}
                    </Avatar>
                    <span style={{ fontWeight: 500, flex: 1 }}>{member.name}</span>
                    {isCrewLead && (
                      <Tag color="gold">Crew Lead</Tag>
                    )}
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      {formatTime(totalMemberTime)}
                    </span>
                  </div>
                ),
                children: (
                  <div style={{ paddingLeft: '44px' }}>
                    {Object.entries(workareaGroups).map(([workareaId, group], index, arr) => {
                      const hasNoWorkarea = parseInt(workareaId) === 0
                      return (
                        <div
                          key={workareaId}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '8px 0',
                            borderBottom: index < arr.length - 1 ? '1px solid #f0f0f0' : 'none'
                          }}
                        >
                          <span style={{ color: '#666' }}>
                            {hasNoWorkarea ? 'No Associated Work Areas' : getWorkareaName(parseInt(workareaId))}
                          </span>
                          <span style={{ color: '#52c41a' }}>
                            {formatTime(group.totalTime)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )
              }
            })}
          />
        </Card>
      ) : (
        <div style={{
          border: '2px dashed #d9d9d9',
          borderRadius: '6px',
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: 'transparent'
        }}>
          <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>No worked crew</Text>
        </div>
      )}
    </div>
  </>
}

export default JobDrawerWorkedCrew
